import { Add, Edit } from '@mui/icons-material';
import { Button, Divider, FormControlLabel, Switch, useMediaQuery, Box } from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Button as SMButton,
  OrderManagementPage,
  PageTitleWithButton,
  Table
} from '../../../components';
import { UserContext } from '../../../context';
import { getPlaceOrderDescriptions } from '../../../fetch';
import { IDescription } from '../../../models';
import { MobileOrderDescription } from './MobileOrderDescription';
import { OrderDescriptionModal } from './OrderDescriptionModal';

export const OrderDescriptions = () => {
  const { isBillingAdmin, isFinancialAdmin, isGlobalAdmin } = useContext(UserContext);
  const { enqueueSnackbar } = useSnackbar();
  const [currentOrderDescription, setCurrentOrderDescriptions] = useState<IDescription | null>(
    null
  );
  const [orderDescriptions, setOrderDescriptions] = useState<IDescription[]>([]);
  const [isLoadingOrderDescriptions, setLoadingOrderDescriptions] = useState<boolean>(false);
  const [isActive, setActive] = useState<boolean>(true);
  const [isModalOpen, setModalOpen] = useState<boolean>(false);

  const isSmMobile = useMediaQuery('(max-width: 600px)');

  const fetchOrderDescriptions = async () => {
    try {
      setLoadingOrderDescriptions(true);
      const res = await getPlaceOrderDescriptions({
        isActive
      });
      setOrderDescriptions(res);
    } catch (error) {
      enqueueSnackbar('Error loading order types, please refresh and try again.', {
        variant: 'error'
      });
      console.error(error);
    } finally {
      setLoadingOrderDescriptions(false);
    }
  };

  useEffect(() => {
    fetchOrderDescriptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  const handleEdit = (description: IDescription) => {
    setModalOpen(true);
    setCurrentOrderDescriptions(description);
  };

  const columns = useMemo(() => {
    return [
      {
        Header: 'Name',
        accessor: 'name',
        sort: false
      },
      {
        Header: 'Description',
        accessor: 'additionalDetails',
        sort: false
      },
      (isBillingAdmin || isFinancialAdmin || isGlobalAdmin) && {
        id: 'actions',
        Header: '',
        accessor: '',
        overrideWidth: 200,
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IDescription } };
        }) => {
          return (
            <Box textAlign="right">
              <Button
                color="primary"
                startIcon={<Edit />}
                onClick={() => {
                  handleEdit(original);
                }}
              >
                Edit
              </Button>
            </Box>
          );
        }
      }
    ].filter(Boolean);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderDescriptions]);

  return (
    <OrderManagementPage title="Order Descriptions">
      <PageTitleWithButton
        pageTitleProps={{
          marginBottom: false,
          showDivider: false,
          title: 'Order Descriptions'
        }}
        button={
          (isBillingAdmin || isFinancialAdmin || isGlobalAdmin) && (
            <Box display="flex" gap={1}>
              <Box mr={-1}>
                <FormControlLabel
                  control={
                    <Switch
                      color="primary"
                      checked={isActive}
                      id="active-inactive"
                      disabled={isLoadingOrderDescriptions}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setActive(e.target.checked);
                      }}
                    />
                  }
                  label="Active"
                />
              </Box>
              <Divider orientation="vertical" flexItem />
              <SMButton
                disabled={isLoadingOrderDescriptions}
                id="create-new-template"
                startIcon={<Add />}
                onClick={() => setModalOpen(true)}
                color="primary"
                variant="contained"
              >
                Add New Description
              </SMButton>
            </Box>
          )
        }
      />
      <Table
        columns={columns}
        data={orderDescriptions}
        isLoading={isLoadingOrderDescriptions}
        hidePagination
        ResponsiveComponent={isSmMobile ? MobileOrderDescription : undefined}
        mobileProps={{
          handleEdit: (type: IDescription) => handleEdit(type)
        }}
      />
      <OrderDescriptionModal
        open={isModalOpen}
        onClose={() => {
          setCurrentOrderDescriptions(null);
          setModalOpen(false);
        }}
        fetchOrderDescriptions={fetchOrderDescriptions}
        currentOrderDescription={currentOrderDescription}
      />
    </OrderManagementPage>
  );
};
